// Content types — canonical definition lives in @lobu/connector-sdk
import type { ContentItem } from "@lobu/connector-sdk";
import {
	keepPreviousData,
	useInfiniteQuery,
	useQuery,
} from "@tanstack/react-query";
import { getFilterIdentity } from "@/components/entity-tabs/events-tab/navigation";
import { useAuthState } from "@/lib/auth-state";
import type { PartialEventFilters } from "../event-filters";
import { convertFiltersToApiParams, isDateFeedMode } from "../event-filters";
import {
	CONTENT_QUERY_ROOT,
	PUBLIC_CONTENT_QUERY_ROOT,
} from "./content-query-keys";
import {
	API_URL,
	apiCall,
	apiErrorFor,
	fetchWithTimeout,
	normalizeOrgContext,
	resolveApiScope,
	resolveOrgSelector,
} from "./core";
import { createQuery } from "./hook-factory";

export type { ContentItem };

type ClassificationStats = Record<string, Record<string, number>>;

interface ExtendedContentListResult {
	content: ContentItem[];
	/**
	 * Exact match count. `null` when the request sent `include_total: false`
	 * (every feed page does — the count is the stats query's job, see
	 * `useContentStats`); `page.has_more` stays exact either way.
	 */
	total: number | null;
	classification_stats?: ClassificationStats;
	timeline?: Array<{ date: string; count: number }>;
	page: {
		limit: number;
		offset: number;
		has_more: boolean;
		has_older?: boolean;
		has_newer?: boolean;
	};
}

/** The feed's aggregate numbers: one request per filter identity. */
interface ContentStats {
	total: number | null;
	classification_stats: ClassificationStats;
}

function appendQueryParam(
	params: URLSearchParams,
	key: string,
	value: unknown,
) {
	if (value === undefined || value === null) return;
	if (Array.isArray(value)) {
		if (value.length === 0) return;
		params.set(key, value.join(","));
		return;
	}
	if (typeof value === "object") {
		params.set(key, JSON.stringify(value));
		return;
	}
	params.set(key, String(value));
}

async function fetchPublicContent(
	orgSlug: string | null | undefined,
	params: Record<string, unknown>,
): Promise<ExtendedContentListResult> {
	const scope = resolveApiScope({ slug: orgSlug ?? null });
	const searchParams = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		appendQueryParam(searchParams, key, value);
	}
	const response = await fetchWithTimeout(
		`${API_URL}/api/${scope.slug}/public/knowledge/search?${searchParams.toString()}`,
		{ credentials: "include" },
	);
	if (!response.ok) throw await apiErrorFor(response);
	return (await response.json()) as ExtendedContentListResult;
}

// ============================================================
// Infinite scroll content queries
// ============================================================

const PAGE_SIZE = 50;
const STATS_STALE_TIME_MS = 5 * 60_000;
/**
 * How often live content changes may re-read the stats on screen. The exact
 * count can take seconds of database time on a large org, so it must not run
 * once per write the way the paginated list does.
 */
export const STATS_LIVE_REFRESH_MS = 30_000;

type InfinitePageParam = null | {
	before_occurred_at?: string;
	before_id?: number;
	offset?: number;
};

function getNextPageParam(
	lastPage: ExtendedContentListResult,
	isDateFeed: boolean,
): InfinitePageParam | undefined {
	if (isDateFeed) {
		if (!lastPage.page.has_older) return undefined;
		const lastItem = lastPage.content[lastPage.content.length - 1];
		if (!lastItem?.occurred_at) return undefined;
		return { before_occurred_at: lastItem.occurred_at, before_id: lastItem.id };
	}
	if (!lastPage.page.has_more) return undefined;
	return { offset: lastPage.page.offset + lastPage.page.limit };
}

/** Base filters minus any cursor/page — callers page through `pageParam`. */
function withoutScrollPosition(
	baseFilters: PartialEventFilters,
): PartialEventFilters {
	const filtersForApi: PartialEventFilters = { ...baseFilters };
	delete filtersForApi.page;
	delete filtersForApi.beforeOccurredAt;
	delete filtersForApi.beforeId;
	delete filtersForApi.afterOccurredAt;
	delete filtersForApi.afterId;
	return filtersForApi;
}

/**
 * Feed page params. Org and public feeds skip the exact COUNT(*) and the
 * classification aggregation (`include_total: false`, no
 * `include_classification`): those cost seconds on large orgs on EVERY page
 * and only the stats query needs them. The account route (`/me`) keeps its
 * original params — its server schema is separate and rejects unknown keys.
 */
function buildInfiniteApiParams(
	baseFilters: PartialEventFilters,
	entityId: number | undefined,
	pageParam: InfinitePageParam,
	isAccount: boolean,
): Record<string, unknown> {
	const filtersForApi = withoutScrollPosition(baseFilters);
	if (pageParam) {
		if (pageParam.before_occurred_at != null && pageParam.before_id != null) {
			filtersForApi.beforeOccurredAt = pageParam.before_occurred_at;
			filtersForApi.beforeId = pageParam.before_id;
		}
	}

	const offset = pageParam?.offset;
	const params = convertFiltersToApiParams(filtersForApi, entityId, {
		limit: PAGE_SIZE,
		...(offset != null ? { offset } : {}),
		...(isAccount ? { includeClassification: "summary" as const } : {}),
	});
	return isAccount ? params : { ...params, include_total: false };
}

/** Stats params: same filters, one row, exact total + classification summary. */
function buildStatsApiParams(
	baseFilters: PartialEventFilters,
	entityId: number | undefined,
): Record<string, unknown> {
	return {
		...convertFiltersToApiParams(withoutScrollPosition(baseFilters), entityId, {
			limit: 1,
			includeClassification: "summary",
		}),
		include_total: true,
	};
}

function toContentStats(result: ExtendedContentListResult): ContentStats {
	return {
		total: result.total,
		classification_stats: result.classification_stats ?? {},
	};
}

export function useInfiniteContentWithFilters(
	organizationId?: string | null,
	entityId?: number,
	filters?: PartialEventFilters,
	orgSlug?: string,
) {
	const { session, isReady } = useAuthState();
	const isAccount = orgSlug === "me";
	const actorId = isAccount && isReady ? session?.user.id : undefined;
	const ctx = normalizeOrgContext(orgSlug ? { slug: orgSlug } : undefined);
	const hasContext = !!(ctx.organizationId || ctx.slug);
	const isDateFeed = filters ? isDateFeedMode(filters) : false;
	const filterIdentity = filters ? getFilterIdentity(filters) : null;

	const canReadAccount =
		!!actorId &&
		(!!filters?.contentIds?.length ||
			(!!filters?.clientIds?.length && !!filters?.mcpActivityId));

	return useInfiniteQuery({
		queryKey: [
			CONTENT_QUERY_ROOT,
			ctx.organizationId,
			ctx.slug,
			entityId,
			filterIdentity,
			actorId,
		],
		queryFn: async ({ pageParam }) =>
			apiCall<ExtendedContentListResult>(
				"read_knowledge",
				buildInfiniteApiParams(filters ?? {}, entityId, pageParam, isAccount),
				resolveOrgSelector(ctx),
			),
		initialPageParam: null as InfinitePageParam,
		getNextPageParam: (lastPage) => getNextPageParam(lastPage, isDateFeed),
		enabled:
			!!filters &&
			(isAccount ? canReadAccount : !!organizationId && hasContext),
		staleTime: 30000,
	});
}

export function usePublicInfiniteContentWithFilters(
	orgSlug?: string | null,
	entityId?: number,
	filters?: PartialEventFilters,
) {
	const isDateFeed = filters ? isDateFeedMode(filters) : false;
	const filterIdentity = filters ? getFilterIdentity(filters) : null;

	return useInfiniteQuery({
		queryKey: [PUBLIC_CONTENT_QUERY_ROOT, orgSlug, entityId, filterIdentity],
		queryFn: async ({ pageParam }) =>
			fetchPublicContent(
				orgSlug,
				buildInfiniteApiParams(filters ?? {}, entityId, pageParam, false),
			),
		initialPageParam: null as InfinitePageParam,
		getNextPageParam: (lastPage) => getNextPageParam(lastPage, isDateFeed),
		enabled: !!orgSlug && !!filters,
		staleTime: 30000,
	});
}

/**
 * Exact total + classification counts for the feed's filters — ONE request
 * per (scope, actor, entity, filter identity), never per page. Keyed under
 * `CONTENT_QUERY_ROOT` so every path that invalidates the feed (SSE
 * `invalidate`, run approvals) refreshes it too, but at its own `"stats"` slot
 * so it never shares an entry with the infinite list. Cursors and `page` are
 * excluded from both key and params: scrolling never refetches the count.
 * Never runs for the account route (`/me`), whose schema has no stats args.
 */
export function useContentStats(
	organizationId?: string | null,
	entityId?: number,
	filters?: PartialEventFilters,
	orgSlug?: string,
) {
	const { session, isReady } = useAuthState();
	const isAccount = orgSlug === "me";
	const actorId = isReady ? session?.user.id : undefined;
	const ctx = normalizeOrgContext(orgSlug ? { slug: orgSlug } : undefined);
	const hasContext = !!(ctx.organizationId || ctx.slug);
	const filterIdentity = filters ? getFilterIdentity(filters) : null;

	return useQuery({
		queryKey: [
			CONTENT_QUERY_ROOT,
			"stats",
			ctx.organizationId,
			ctx.slug,
			entityId,
			filterIdentity,
			actorId,
		],
		queryFn: async () =>
			toContentStats(
				await apiCall<ExtendedContentListResult>(
					"read_knowledge",
					buildStatsApiParams(filters ?? {}, entityId),
					resolveOrgSelector(ctx),
				),
			),
		enabled: !!filters && !isAccount && !!organizationId && hasContext,
		staleTime: STATS_STALE_TIME_MS,
		meta: { liveRefreshIntervalMs: STATS_LIVE_REFRESH_MS },
		placeholderData: keepPreviousData,
	});
}

export function usePublicContentStats(
	orgSlug?: string | null,
	entityId?: number,
	filters?: PartialEventFilters,
) {
	const filterIdentity = filters ? getFilterIdentity(filters) : null;

	return useQuery({
		queryKey: [
			PUBLIC_CONTENT_QUERY_ROOT,
			"stats",
			orgSlug,
			entityId,
			filterIdentity,
		],
		queryFn: async () =>
			toContentStats(
				await fetchPublicContent(
					orgSlug,
					buildStatsApiParams(filters ?? {}, entityId),
				),
			),
		enabled: !!orgSlug && orgSlug !== "me" && !!filters,
		staleTime: STATS_STALE_TIME_MS,
		meta: { liveRefreshIntervalMs: STATS_LIVE_REFRESH_MS },
		placeholderData: keepPreviousData,
	});
}

// ============================================================
// Content Distribution (Timeline)
// ============================================================

interface ContentDistributionResult {
	distribution: Array<{ date: string; count: number; platform?: string }>;
}

export function useContentDistribution(
	organizationId?: string | null,
	options?: {
		entityId?: number;
		connectionIds?: number[];
		groupByPlatform?: boolean;
	},
	orgContext?: string | { slug?: string | null },
) {
	return useQuery({
		queryKey: [
			"content-distribution",
			organizationId,
			options?.entityId,
			options?.connectionIds,
			options?.groupByPlatform,
			typeof orgContext === "string" ? orgContext : orgContext?.slug,
		],
		queryFn: async () => {
			const params = new URLSearchParams();
			if (options?.connectionIds && options.connectionIds.length > 0) {
				params.append("connection_ids", options.connectionIds.join(","));
			}
			if (options?.groupByPlatform) {
				params.append("group_by_platform", "true");
			}
			const queryString = params.toString();
			const scope = resolveApiScope(orgContext);
			const url = `${API_URL}/api/${scope.slug}/entities/${options?.entityId}/content-distribution${queryString ? `?${queryString}` : ""}`;

			const response = await fetchWithTimeout(url, {
				method: "GET",
				credentials: "include",
			});

			if (!response.ok) {
				throw await apiErrorFor(response);
			}

			const result: ContentDistributionResult = await response.json();
			return result.distribution || [];
		},
		enabled: !!organizationId && !!options?.entityId,
		staleTime: 60000,
	});
}

// ============================================================
// Classifiers
// ============================================================

interface Classifier {
	id: number;
	slug: string;
	name: string;
	description?: string;
	attribute_key: string;
	current_version: number;
	attribute_values: Record<
		string,
		{
			description?: string;
			examples?: string[];
			parent?: Record<string, string>;
		}
	>;
	is_inherited?: boolean;
	automation_id?: number | null;
	automation_name?: string | null;
}

interface ClassifiersResult {
	classifiers: Classifier[];
}

export const useEntityClassifiers = createQuery<
	[organizationId?: string | null, entityId?: number],
	Classifier[]
>({
	queryKey: (organizationId, entityId) => [
		"classifiers",
		organizationId,
		entityId,
	],
	tool: "manage_classifiers",
	body: (_organizationId, entityId) => ({
		action: "list",
		entity_id: entityId,
	}),
	transform: (r) => r.data?.classifiers || [],
	enabled: (organizationId, entityId) => !!organizationId && !!entityId,
	staleTime: 60000,
});

export function usePublicEntityClassifiers(
	orgSlug?: string | null,
	entityId?: number,
) {
	return useQuery({
		queryKey: ["public-classifiers", orgSlug, entityId],
		queryFn: async () => {
			const scope = resolveApiScope({ slug: orgSlug ?? null });
			const params = new URLSearchParams();
			if (entityId) params.set("entity_id", String(entityId));

			const response = await fetchWithTimeout(
				`${API_URL}/api/${scope.slug}/public/classifiers${params.toString() ? `?${params}` : ""}`,
				{
					credentials: "include",
				},
			);
			if (!response.ok) {
				throw await apiErrorFor(response);
			}
			const result = (await response.json()) as ClassifiersResult & {
				data?: { classifiers?: Classifier[] };
			};
			return result.data?.classifiers ?? [];
		},
		enabled: !!orgSlug && !!entityId,
		staleTime: 60000,
	});
}
